import { Extension, InputRule, getNodeType } from "@tiptap/core";
import type { Node, ResolvedPos } from "@tiptap/pm/model";
import { sinkListItem } from "@tiptap/pm/schema-list";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { continuousListStarts } from "../../lib/listNumbering.ts";

const listNames = new Set(["orderedList", "bulletList", "taskList"]);
const itemNames = new Set(["listItem", "taskItem"]);
const numberingKey = new PluginKey<DecorationSet>("ternListNumbering");

function listStartChanges(doc: Node) {
  const changes: { node: Node; pos: number; start: number }[] = [];
  function visit(parent: Node, from: number) {
    if (parent.isTextblock || !parent.childCount) return;
    const starts = continuousListStarts(parent.content.content.map((node) => node.type.name === "orderedList"
      ? { kind: "ordered", start: node.attrs.start as number, count: node.childCount, marker: node.attrs.type as string | null }
      : { kind: listNames.has(node.type.name) ? "unordered" : "break" }));
    parent.forEach((node, offset, index) => {
      const pos = from + offset;
      const start = starts[index];
      if (start != null && start !== node.attrs.start) changes.push({ node, pos, start });
      visit(node, pos + 1);
    });
  }
  visit(doc, 0);
  return changes;
}

function numberDecorations(doc: Node) {
  return DecorationSet.create(doc, listStartChanges(doc).map(({ node, pos, start }) =>
    Decoration.node(pos, pos + node.nodeSize, { start: String(start) }),
  ));
}

function listDepth(position: ResolvedPos, through = position.depth) {
  let depth = 0;
  for (let i = 1; i <= through; i++) if (listNames.has(position.node(i).type.name)) depth++;
  return depth;
}

function nestedDepth(node: Node): number {
  let childDepth = 0;
  node.forEach((child) => { childDepth = Math.max(childDepth, nestedDepth(child)); });
  return childDepth + Number(listNames.has(node.type.name));
}

/** Keep one child level and continuous sibling numbering without moving any content. */
export const ListBehavior = Extension.create({
  name: "ternListBehavior",
  onCreate() {
    // Also normalize lists loaded from existing editor JSON. Locked documents
    // only receive display decorations; their stored content is not changed.
    if (this.editor.isEditable) this.editor.view.dispatch(this.editor.state.tr);
  },
  addProseMirrorPlugins() {
    const editor = this.editor;
    return [new Plugin({
      key: numberingKey,
      state: {
        init: (_, state) => numberDecorations(state.doc),
        apply: (tr, decorations) => tr.docChanged ? numberDecorations(tr.doc) : decorations,
      },
      props: { decorations: (state) => numberingKey.getState(state) },
      appendTransaction(_transactions, _oldState, state) {
        if (!editor.isEditable) return null;
        const changes = listStartChanges(state.doc);
        if (!changes.length) return null;
        const tr = state.tr.setMeta("addToHistory", false);
        for (const { pos, start } of changes) tr.setNodeAttribute(pos, "start", start);
        return tr;
      },
    })];
  },
  addCommands() {
    return {
      sinkListItem: (typeOrName) => ({ state, dispatch }) => {
        const type = getNodeType(typeOrName, state.schema);
        const { $from, $to } = state.selection;
        const range = $from.blockRange($to, (node) => !!node.childCount && node.firstChild!.type === type);
        if (!range) return false;
        const depth = listDepth($from, range.depth);
        // Moving a parent also moves its children. Never turn those into a third level.
        for (let i = range.startIndex; i < range.endIndex; i++) {
          if (depth + 1 + nestedDepth(range.parent.child(i)) > 2) return false;
        }
        return sinkListItem(type)(state, dispatch);
      },
    };
  },
  addKeyboardShortcuts() {
    return {
      Tab: () => {
        const { $from } = this.editor.state.selection;
        for (let depth = $from.depth; depth > 0; depth--) {
          const name = $from.node(depth).type.name;
          if (!itemNames.has(name)) continue;
          this.editor.commands.sinkListItem(name);
          // At the limit, retain the caret instead of tabbing to a different control.
          return true;
        }
        return false;
      },
    };
  },
  addInputRules() {
    return [new InputRule({
      find: /^\s*([-+*]|\d+\.)\s$/,
      handler: ({ state, range, match }) => {
        const { $from } = state.selection;
        if (listDepth($from) < 2) return null;
        // After Enter → Tab, typing "- " changes the child list only. Wrapping
        // its first paragraph would fail the schema or introduce another level.
        for (let depth = $from.depth; depth > 0; depth--) {
          const list = $from.node(depth);
          if (!listNames.has(list.type.name)) continue;
          if (list.type.name === "taskList") return null;
          const type = state.schema.nodes[/\d/.test(match[1]!) ? "orderedList" : "bulletList"]!;
          state.tr.delete(range.from, range.to).setNodeMarkup($from.before(depth), type);
          return;
        }
        return null;
      },
    })];
  },
});
